import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Box from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Box';
import { withStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import fr from 'facet-rest';
import MetaFormInline from 'Components/UI/Meta/MetaForm/Components/MetaFormInline';
import { usePageContentContext } from 'Views/Page/Contexts/PageContentContext';
import StepNavigator from 'Components/ComponentLibrary/Molecules/StepNavigator';
import Typography from 'Components/UI/Typography';

const styles = (theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    flex: 1,
    justifyContent: 'space-between',
  },
  blueSubtitle: {
    fontWeight: theme.typography.fontWeightBold,
    textAlign: 'center',
    color: theme.palette.facet.accentBlue19,
  },
  navigator: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%',
  },
});

const WhatIfWizardCollectionStepperTemplateContent = ({
  className,
  classes,
  layout,
  metaOverrideSet,
  resource,
  index,
  total,
  finished,
  next,
  previous,
  isStart,
  isEnd,
  setResourceAtIndex,
}) => {
  const {
    pageContentResource,
    setPageContentCollection,
    loadPageContentLink,
    isPageContentLoading,
  } = usePageContentContext();
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState(null);
  const previousLink = pageContentResource
    .relationship(fr.relationships.previousLink.name)
    ?.link('related');

  const onNext = async () => {
    setLoading(true);
    try {
      const [updatedResource] = await resource.submit();
      setResourceAtIndex(updatedResource, index - 1);
      if (isEnd) {
        const updatedColl = await pageContentResource.submit();
        await setPageContentCollection(updatedColl);
        finished();
      } else {
        next();
      }
    } catch (e) {
      setErrors(e.errors);
      setLoading(false);
    }
    setLoading(false);
  };

  const onPrevious = async () => {
    setLoading(true);
    try {
      if (isStart) {
        await loadPageContentLink(previousLink);
      } else {
        previous();
      }
    } catch (e) {
      setErrors(e.errors);
      setLoading(false);
    }
    setLoading(false);
  };

  return (
    <Box className={classNames(className, classes.root)}>
      <MetaFormInline
        fullWidth
        padding={'none'}
        key={resource.id()}
        noIndividualSubmitButtons
        resource={resource}
        layout={layout}
        metaOverrideSet={metaOverrideSet}
        LayoutProps={{
          index,
          total,
          isStart,
          isEnd,
        }}
        errorsOverride={errors}
      />
      <Box className={classes.navigator}>
        <Typography
          className={classes.blueSubtitle}
          variant={'h6'}
          color={'primary'}
        >
          {`${index} of ${total}`}
        </Typography>
        <StepNavigator
          disabled={isPageContentLoading || loading}
          isLoading={isPageContentLoading || loading}
          onNext={onNext}
          onPrevious={onPrevious}
        />
      </Box>
    </Box>
  );
};

WhatIfWizardCollectionStepperTemplateContent.defaultProps = {
  className: '',
  metaOverrideSet: undefined,
};

WhatIfWizardCollectionStepperTemplateContent.propTypes = {
  className: PropTypes.string,
  classes: PropTypes.object.isRequired,
  /** layout: The body content layout * */
  layout: PropTypes.object.isRequired,
  /** metaOverrideSet: A meta override set * */
  metaOverrideSet: PropTypes.object,
  /** resource: The displayed resource. Provied by withCollectionStepper HOC * */
  resource: PropTypes.instanceOf(fr.Collection).isRequired,
  /** index: The index of the current resource in the collection. One based numbering (arrayIndex+1). Provied by withCollectionStepper HOC * */
  index: PropTypes.number.isRequired,
  /** total: The total resources in the collection. Provied by withCollectionStepper HOC * */
  total: PropTypes.number.isRequired,
  /** finished: Function to notify stepper to teardown. Provied by withCollectionStepper HOC * */
  finished: PropTypes.func.isRequired,
  /** next: Function to load the next resource. Provied by withCollectionStepper HOC * */
  next: PropTypes.func.isRequired,
  /** previous: Function to load the previous resource. Provied by withCollectionStepper HOC * */
  previous: PropTypes.func.isRequired,
  /** isStart: True if the current resource is the first resource in the collection. Provied by withCollectionStepper HOC * */
  isStart: PropTypes.bool.isRequired,
  /** isEnd: True if the current resource is the last resource in the collection. Provied by withCollectionStepper HOC * */
  isEnd: PropTypes.bool.isRequired,
  /** finished: Function to update resource at index. Used to update a resource after submitting it. Provided by withCollectionStepper HOC * */
  setResourceAtIndex: PropTypes.func.isRequired,
};

export default withStyles(styles)(WhatIfWizardCollectionStepperTemplateContent);
